import React, { useCallback, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FormattedMessage, useIntl } from 'react-intl';

import AddSubTask from '../ticket-drawer/AddSubTask';
import SubTask from '../ticket-drawer/SubTask';
import { addTaskAction, editTaskAction } from '../../../../redux/tasks/actions';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { getOpenedTaskExtendedCustomFields, getSubTaskOrder } from '../../../../redux/tasks/taskEntities/selectors';

const TaskSubtaskSection = ({ AddButtonLabel, isGuest, orderedSubtaskIdList, taskId, extendedCustomFields }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [draggingSubtaskId, setDraggingSubtaskId] = useState(null);

  const handleSubmit = async ({ taskName, taskAssignee = [], taskDate = '' }) => {
    if (!taskName) {
      setShowAddSubtask(false);
      return;
    }
    await dispatch(addTaskAction({
      parentTaskId: taskId,
      taskTitle: taskName,
      assignee: taskAssignee?.map(a => ({
        assigneeId: {
          _id: a?.value,
          ...a
        }
      })),
      dueDate: taskDate,
      projectLess: true,
      customFields: extendedCustomFields || [],
    }));
  };

  const handleDragStart = (start) => {
    setDraggingSubtaskId(start.draggableId);
  };

  const onDragEnd = (result) => {
    setDraggingSubtaskId(null);
    if (!result.destination) return;

    const updatedSubtasks = [...orderedSubtaskIdList];
    const [movedItem] = updatedSubtasks.splice(result.source.index, 1);
    updatedSubtasks.splice(result.destination.index, 0, movedItem);

    const subTaskOrder = updatedSubtasks;

    dispatch(
      editTaskAction({
        _id: taskId,
        subTaskOrder
      })
    );
  };

  const closeAddSubtask = useCallback(() => setShowAddSubtask(false), []);
  const openAddSubtask = useCallback(() => setShowAddSubtask(true), []);
  const triggerShowSubtask = useCallback(() => setShowAddSubtask(showAddSubtask => !showAddSubtask), []);

  return (
    <>
      <div className="w-full mt-5 mb-2">
        <div className="flex justify-between align-center">
          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(15,15,25,0.35)' }}>
            <FormattedMessage id="SUBTASKS" defaultMessage="Subtasks" />
          </div>
        </div>
      </div>
      <div className="w-full mb-5">
        {orderedSubtaskIdList?.length === 0 && !showAddSubtask && (
          <div className="text-[12px] mb-2" style={{ color: 'rgba(15,15,25,0.35)' }}>
            <FormattedMessage id="NO_SUBTASKS" defaultMessage="No Subtasks" />
          </div>
        )}
        <DragDropContext onDragStart={handleDragStart} onDragEnd={onDragEnd}>
          <Droppable droppableId="subtaskList" direction="vertical">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {orderedSubtaskIdList?.map((subTaskId, index) => (
                  <Draggable key={subTaskId} draggableId={subTaskId} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="flex draggable-subtask-row flex-row items-center w-full relative"
                      >
                        <div
                          className={`-mt-2 absolute -left-5 subtask-options-row-drag-indicator${snapshot.isDragging
                            ? " isDragging"
                            : draggingSubtaskId && draggingSubtaskId !== subTaskId
                              ? ""
                              : " hover-drag-icon"
                            }`}
                          {...provided.dragHandleProps}
                        >
                          <DragIndicatorIcon fontSize="small" color="rgba(0, 0, 0, 0.87)" />
                        </div>
                        <SubTask subTaskId={subTaskId} openAddSubtask={openAddSubtask} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {showAddSubtask && (
          <AddSubTask
            handleSubmit={handleSubmit}
            onClose={closeAddSubtask}
          />
        )}
        <div className="add-subtasks-btn-wrapper">
          <AddButtonLabel
            title={intl.formatMessage({ id: showAddSubtask ? 'CANCEL' : 'ADD_SUBTASK', defaultMessage: 'Add Subtask' })}
            onClick={triggerShowSubtask}
            showIcon={!showAddSubtask}
            isGuest={isGuest}
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  isGuest: state?.userReducer?.role === 'guest',
  orderedSubtaskIdList: getSubTaskOrder(state, ownProps),
  extendedCustomFields: getOpenedTaskExtendedCustomFields(state)
});

export default connect(mapStateToProps)(TaskSubtaskSection);
